import { RA_VESSEL_GROUPS } from '@felix/domain';
import { Button, Checkbox, Modal, Select, TextInput } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconLibraryPlus, IconSearch } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFitColumns } from '../../../fit-columns.js';
import { StatusBadge } from '../../../StatusBadge.js';
import table from '../../materials/Materials.module.css';
import { errorCode } from '../../personnel/source.js';
import purchasing from '../../purchasing/Purchasing.module.css';
import { sortItems } from '../../purchasing/requisitions.js';
import {
  LoadError,
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from '../../purchasing/table.js';
import {
  DEFAULT_LIBRARY_FILTER,
  type DefaultLibraryFilter,
  filterDefaultLibrary,
  nodeOptions,
  selectableCodes,
} from './default-library.js';
import classes from './RaMaster.module.css';
import type {
  RaDefaultActivity,
  RaDefaultLibraryView,
  RaDefaultPullResult,
  RaMasterSource,
} from './types.js';

/**
 * Risk ▸ Master Data ▸ Default library (ADR 0106 §2): ürünün varsayılan RA kütüphanesinden
 * faaliyet seçip "Add to company library" ile şirketin ana verisine **taslak** olarak çekme.
 * Süzgeç: arama, operasyon alanı (alt alanlarıyla), System / Component, gemi tipi, kütüphanede
 * olup olmadığı. Kütüphanede olan faaliyet işaretlidir ve seçilemez (tekrar çekme kopya üretmez).
 * Çekilen faaliyet teknik inceleme kapısındadır; olasılık/şiddet boş gelir (ADR 0098 §4–§5).
 */

const PAGE_SIZE = 12;

type SortKey = 'code' | 'activity' | 'area' | 'hazards' | 'status';

const SORTS: Record<SortKey, (a: RaDefaultActivity) => string | number> = {
  code: (a) => a.code,
  activity: (a) => a.name,
  area: (a) => a.areaName,
  hazards: (a) => a.candidateCount,
  status: (a) => (a.inLibrary ? 1 : 0),
};

/** Yüklenmiş katalog (ekran testi doğrudan çizer). */
export function DefaultLibraryBrowser({
  view,
  busy,
  error,
  initialSelection = [],
  onAdd,
  onClose,
}: {
  view: RaDefaultLibraryView;
  busy: boolean;
  error: string | null;
  initialSelection?: readonly string[];
  onAdd: (codes: string[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskMaster.defaultLibrary.${key}`, options);
  const [filter, setFilter] = useState<DefaultLibraryFilter>(DEFAULT_LIBRARY_FILTER);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set(initialSelection));
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState<SortKey>>({ key: 'code', dir: 'asc' });
  const tableBox = useFitColumns<HTMLElement>();

  const items = useMemo(
    () => sortItems(filterDefaultLibrary(view, filter), sort.dir, SORTS[sort.key]),
    [view, filter, sort],
  );
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const matching = selectableCodes(items);
  const pageCodes = selectableCodes(visible);
  const pageChecked = pageCodes.length > 0 && pageCodes.every((c) => selected.has(c));
  const pageMixed = !pageChecked && pageCodes.some((c) => selected.has(c));
  const nodes = nodeOptions(view, filter.area);
  const inLibrary = view.activities.filter((a) => a.inLibrary).length;

  const change = (patch: Partial<DefaultLibraryFilter>) => {
    setFilter((f) => ({ ...f, ...patch }));
    setPage(1);
  };
  const toggle = (codes: readonly string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const code of codes) {
        if (on) next.add(code);
        else next.delete(code);
      }
      return next;
    });
  const head = (key: SortKey, label: string, drop?: number) => (
    <SortableTh
      column={key}
      label={label}
      sort={sort}
      onSort={(next) => {
        setSort(next);
        setPage(1);
      }}
      drop={drop}
    />
  );

  return (
    <div className={classes.defaultBody}>
      <p className={classes.hint}>
        {k('help', { version: view.version, count: view.activities.length, inLibrary })}
      </p>
      <p className={classes.note}>{k('draftNote')}</p>
      <div className={classes.defaultToolbar}>
        <TextInput
          className={classes.search}
          leftSection={<IconSearch size={16} aria-hidden />}
          placeholder={k('search')}
          aria-label={k('search')}
          value={filter.query}
          onChange={(event) => change({ query: event.currentTarget.value })}
        />
        <Select
          className={classes.filter}
          aria-label={k('filter.area')}
          allowDeselect={false}
          searchable
          value={filter.area}
          data={[
            { value: 'all', label: k('filter.allAreas') },
            ...view.areas.map((a) => ({ value: a.code, label: `${a.code} · ${a.name}` })),
          ]}
          onChange={(value) => change({ area: value ?? 'all', node: 'all' })}
        />
        <Select
          className={classes.filter}
          aria-label={k('filter.node')}
          allowDeselect={false}
          searchable
          value={filter.node}
          data={[
            { value: 'all', label: k('filter.allNodes') },
            ...nodes.map((n) => ({ value: n.code, label: `${n.code} · ${n.name}` })),
          ]}
          onChange={(value) => change({ node: value ?? 'all' })}
        />
        <Select
          className={classes.filter}
          aria-label={k('filter.group')}
          allowDeselect={false}
          value={filter.group}
          data={[
            { value: 'all', label: k('filter.allGroups') },
            ...RA_VESSEL_GROUPS.filter((g) => g !== 'ALL').map((g) => ({
              value: g,
              label: t(`riskMaster.groups.${g}`),
            })),
          ]}
          onChange={(value) => change({ group: value ?? 'all' })}
        />
        <Select
          className={classes.filter}
          aria-label={k('filter.show')}
          allowDeselect={false}
          value={filter.show}
          data={(['all', 'available', 'inLibrary'] as const).map((value) => ({
            value,
            label: k(`filter.${value}`),
          }))}
          onChange={(value) => change({ show: (value ?? 'all') as DefaultLibraryFilter['show'] })}
        />
      </div>
      <div className={classes.selectionBar}>
        <strong aria-live="polite">{k('selected', { count: selected.size })}</strong>
        <Button
          variant="subtle"
          size="compact-sm"
          disabled={matching.length === 0}
          onClick={() => toggle(matching, true)}
        >
          {k('selectMatching', { count: matching.length })}
        </Button>
        <Button
          variant="subtle"
          size="compact-sm"
          disabled={selected.size === 0}
          onClick={() => setSelected(new Set())}
        >
          {k('clear')}
        </Button>
      </div>
      {items.length === 0 ? (
        <NoMatch
          title={k('empty')}
          onClear={() => {
            setFilter(DEFAULT_LIBRARY_FILTER);
            setPage(1);
          }}
        />
      ) : (
        <>
          <section
            ref={tableBox}
            className={table.tableWrap}
            aria-label={k('tableLabel')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={`${table.table} ${purchasing.table}`}>
              <thead>
                <tr>
                  <th scope="col" className={classes.checkCell}>
                    <Checkbox
                      aria-label={k('selectPage')}
                      checked={pageChecked}
                      indeterminate={pageMixed}
                      disabled={pageCodes.length === 0}
                      onChange={(event) => toggle(pageCodes, event.currentTarget.checked)}
                    />
                  </th>
                  {head('code', k('columns.code'))}
                  {head('activity', k('columns.activity'))}
                  {head('area', k('columns.area'), 2)}
                  <th scope="col" data-drop={1}>
                    {k('columns.groups')}
                  </th>
                  {head('hazards', k('columns.hazards'), 4)}
                  <th scope="col" data-drop={3}>
                    {k('columns.review')}
                  </th>
                  {head('status', k('columns.status'))}
                </tr>
              </thead>
              <tbody>
                {visible.map((a) => (
                  <tr key={a.code} data-in-library={a.inLibrary || undefined}>
                    <td className={classes.checkCell}>
                      <Checkbox
                        aria-label={k('selectRow', { code: a.code })}
                        checked={a.inLibrary || selected.has(a.code)}
                        disabled={a.inLibrary}
                        onChange={(event) => toggle([a.code], event.currentTarget.checked)}
                      />
                    </td>
                    <td>
                      <strong>{a.code}</strong>
                    </td>
                    <td className={classes.wrapCell}>
                      {a.name}
                      <span className={classes.sub}>{a.nodeName}</span>
                    </td>
                    <td data-drop={2} className={classes.wrapCell}>
                      {a.areaName}
                    </td>
                    <td data-drop={1}>
                      {a.vesselGroups.map((g) => t(`riskMaster.groups.${g}`)).join(', ')}
                      {a.applicabilityReview ? (
                        <span className={classes.sub}>{k('applicabilityReview')}</span>
                      ) : null}
                    </td>
                    <td data-drop={4} className={classes.hazardCell}>
                      {k('hazardCount', { count: a.candidateCount })}
                      <span className={classes.sub}>{a.hazards.slice(0, 2).join('; ')}</span>
                    </td>
                    <td data-drop={3}>
                      {a.queue.length > 0 ? (
                        <span className={classes.chips}>
                          {a.queue.map((q) => (
                            <StatusBadge key={q} tone="warning">
                              {k(`queue.${q}`)}
                            </StatusBadge>
                          ))}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <StatusBadge tone={a.inLibrary ? 'success' : 'neutral'}>
                        {k(a.inLibrary ? 'status.inLibrary' : 'status.available')}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <TablePager
            page={current}
            pages={pages}
            summary={t('riskMaster.showing', {
              from: (current - 1) * PAGE_SIZE + 1,
              to: Math.min(current * PAGE_SIZE, items.length),
              total: items.length,
            })}
            label={t('riskMaster.pagination')}
            onPage={setPage}
          />
        </>
      )}
      {error ? (
        <p className={purchasing.error} role="alert">
          {error}
        </p>
      ) : null}
      <div className={purchasing.dialogActions}>
        <Button variant="default" onClick={onClose}>
          {k('cancel')}
        </Button>
        <Button
          leftSection={<IconLibraryPlus size={16} />}
          disabled={selected.size === 0 || !view.can.pull}
          loading={busy}
          onClick={() => onAdd([...selected])}
        >
          {k('add', { count: selected.size })}
        </Button>
      </div>
    </div>
  );
}

/** Pencere: kataloğu yükler, seçilenleri çeker; sonuç üst ekrana bildirilir. */
export function DefaultLibraryDialog({
  library,
  onClose,
  onDone,
}: {
  library: NonNullable<NonNullable<RaMasterSource['editor']>['defaultLibrary']>;
  onClose: () => void;
  onDone: (result: RaDefaultPullResult) => void;
}) {
  const { t } = useTranslation();
  const narrow = useMediaQuery('(max-width: 767px)');
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; code: string }
    | { kind: 'ready'; view: RaDefaultLibraryView }
  >({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', view: await library.load() });
    } catch (failure) {
      setState({ kind: 'error', code: errorCode(failure) });
    }
  }, [library]);
  useEffect(() => {
    void load();
  }, [load]);

  const add = async (codes: string[]) => {
    setBusy(true);
    setError(null);
    try {
      onDone(await library.pull(codes, false));
    } catch (failure) {
      const detail = (failure as { detail?: unknown } | null)?.detail;
      const code = errorCode(failure);
      const values = { detail: typeof detail === 'string' ? detail : '' };
      setError(
        t(`riskMaster.defaultLibrary.errors.${code}`, {
          ...values,
          defaultValue: t(`riskMaster.errors.${code}`, {
            ...values,
            defaultValue: t('riskMaster.errors.GENERIC'),
          }),
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={t('riskMaster.defaultLibrary.title')}
      size="min(1200px, 96vw)"
      fullScreen={narrow === true}
      closeButtonProps={{ 'aria-label': t('riskMaster.defaultLibrary.cancel') }}
    >
      {state.kind === 'error' ? (
        <LoadError
          title={t('riskMaster.defaultLibrary.error')}
          code={state.code}
          onRetry={() => void load()}
        />
      ) : state.kind === 'loading' ? (
        <TableSkeleton columns={6} label={t('riskMaster.defaultLibrary.title')} />
      ) : (
        <DefaultLibraryBrowser
          view={state.view}
          busy={busy}
          error={error}
          onAdd={(codes) => void add(codes)}
          onClose={onClose}
        />
      )}
    </Modal>
  );
}
